import React from "react";
import { Table } from "@tanstack/react-table";
import { CompaniesApiResponse, CompanyData } from "../../../../utils/types";


interface CompaniesTablePaginationProps {
  table: Table<CompanyData>;
  data: CompaniesApiResponse | undefined;
  setPageIndex: (pageIndex: number) => void;
}

const CompaniesTablePagination: React.FC<CompaniesTablePaginationProps> = ({
  table,
  data,
  setPageIndex,
}) => {
  if (!data || data.users.last_page <= 1) {
    return null;
  }

  const handlePreviousPage = () => {
    if (table.getCanPreviousPage()) {
      const newPageIndex = table.getState().pagination.pageIndex - 1;
      setPageIndex(newPageIndex);
    }
  };

  const handleNextPage = () => {
    if (table.getCanNextPage()) {
      const newPageIndex = table.getState().pagination.pageIndex + 1;
      setPageIndex(newPageIndex);
    }
  };

  const handlePageClick = (pageNumber: number) => {
    setPageIndex(pageNumber - 1);
  };

  const getPageNumbers = () => {
    const currentPage = data.users.current_page;
    const totalPages = data.users.last_page;
    const pages = [];

    if (totalPages > 0) {
      pages.push(1);
    }

    if (currentPage > 4) {
      pages.push(-1); // Ellipsis
    }

    for (
      let i = Math.max(2, currentPage - 2);
      i <= Math.min(totalPages - 1, currentPage + 2);
      i++
    ) {
      if (!pages.includes(i)) {
        pages.push(i);
      }
    }

    if (currentPage < totalPages - 3) {
      pages.push(-2); // Ellipsis
    }

    if (totalPages > 1 && !pages.includes(totalPages)) {
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-100">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Pagination Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={handlePreviousPage}
            disabled={!table.getCanPreviousPage()}
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm ${
              table.getCanPreviousPage()
                ? "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                : "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed"
            }`}
          >
            Previous
          </button>

          <div className="flex items-center gap-1">
            {getPageNumbers().map((pageNum, index) => {
              if (pageNum === -1 || pageNum === -2) {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-2 py-2 text-gray-400"
                  >
                    ...
                  </span>
                );
              }

              const isActive = pageNum === data.users.current_page;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageClick(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    isActive
                      ? "bg-indigo-600 text-white border border-indigo-600"
                      : "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={handleNextPage}
            disabled={!table.getCanNextPage()}
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm ${
              table.getCanNextPage()
                ? "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                : "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed"
            }`}
          >
            Next
          </button>
        </div>

        {/* Pagination Info */}
        <div className="text-sm text-gray-700 flex flex-col sm:flex-row items-center gap-2">
          <span>
            Page {data.users.current_page} of {data.users.last_page}
          </span>
          <span className="hidden sm:inline">•</span>
          <span>
            Showing {data.users.from} to {data.users.to} of {data.users.total}{" "}
            entries
          </span>
        </div>
      </div>

      {/* Mobile-friendly page info */}
      <div className="mt-3 sm:hidden">
        <div className="text-xs text-gray-600 text-center">
          {data.users.from}-{data.users.to} of {data.users.total} results
        </div>
      </div>
    </div>
  );
};

export default CompaniesTablePagination;
